import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { API_BASE_URL } from "../config";
import { useFullScreenDialog } from "../useFullScreenDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const ACTION_COLOR = {
  create: "success",
  update: "info",
  delete: "error",
  read: "default",
  export: "warning",
  import: "secondary",
  share: "secondary",
};

// An SMS send's outcome, as recorded by core/incident_shares.audit_meta.
const smsSummary = (sms) =>
  sms && typeof sms === "object" && typeof sms.sent === "number"
    ? `Texted ${sms.target}: ${sms.sent} of ${sms.recipients} sent`
    : "";

// Short human summary of a row's metadata (changed fields, counts, etc.).
function metaSummary(meta) {
  if (!meta || typeof meta !== "object") return "";
  if (meta.reason === "gdpr_erasure") {
    const n = meta.notes_deleted;
    return `GDPR erasure — personal & clinical details removed${
      typeof n === "number" ? ` (${n} note${n === 1 ? "" : "s"} deleted)` : ""}`;
  }
  if (smsSummary(meta)) return smsSummary(meta);  // a share
  if (Array.isArray(meta.changed) && meta.changed.length) {
    // A reallocation may have texted the new allocatee — same row, so say both.
    const sms = smsSummary(meta.allocation_sms);
    return `Changed: ${meta.changed.join(", ")}${sms ? ` · ${sms}` : ""}`;
  }
  if (typeof meta.created === "number" || typeof meta.skipped === "number") {
    return `Imported ${meta.created ?? 0}, skipped ${meta.skipped ?? 0}`;
  }
  if (meta.bulk && typeof meta.deleted === "number") {
    return `Bulk delete: ${meta.deleted}`;
  }
  return "";
}

/**
 * Audit trail popup for a single object. For incidents it hits the
 * incident-specific endpoint (gated by incident visibility); for anything else
 * it uses the general log filtered by target (Super-Admin only server-side).
 */
export default function AuditTrail({ open, onClose, targetType, targetId, title }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const fullScreen = useFullScreenDialog();

  useEffect(() => {
    if (!open || targetId == null) return;
    const url =
      targetType === "incident"
        ? `${API_BASE_URL}/audit-logs/incident/${targetId}`
        : `${API_BASE_URL}/audit-logs?target_type=${encodeURIComponent(targetType)}&target_id=${targetId}`;
    setLoading(true);
    axios
      .get(url)
      .then((res) => setRows(res.data || []))
      .catch(() => setRows([])) // the global interceptor already surfaces the error
      .finally(() => setLoading(false));
  }, [open, targetType, targetId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>{title || "History"}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">No audit entries recorded.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Who</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{fmt(r.at)}</TableCell>
                  <TableCell>{r.actor_name || r.actor_email || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.action}
                      color={ACTION_COLOR[r.action] || "default"}
                    />
                  </TableCell>
                  <TableCell>{metaSummary(r.meta)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
